import styles from './styles.module.scss';
import { useEffect, useRef, useMemo } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger';
import { buildClassName } from 'utils/build-class-name';
import { useIsMobile, useIsSmallMobile, useIsTablet } from 'hooks/use-size-class';
import { ProgramData } from 'tenants';
import MobileProgramCard from './mobile-program-card';
import ProgramCard from './program-card';

const MARGIN_INCREMENT = 15;

type ProgramsProps = {
  programs: readonly ProgramData[];
  onButtonClick: (index: number) => void;
};

function Programs({ programs, onButtonClick }: ProgramsProps) {  
  const isMobile = useIsMobile();
  const isSmallMobile = useIsSmallMobile();
  const isTablet = useIsTablet();
  const thriveTextRef = useRef(null);

  // Keep references to ScrollTrigger instances and timelines
  // This is crucial for proper cleanup to avoid memory leaks and UI glitches
  // See: https://greensock.com/docs/v3/Plugins/ScrollTrigger#cleanup
  // Also see: https://react.dev/learn/synchronizing-with-effects#triggering-animations
  const scrollTriggersRef = useRef<ScrollTrigger[]>([]);
  const timelinesRef = useRef<gsap.core.Timeline[]>([]);

  const getCardOffsets = useMemo(() => {
    // Define progressive offsets based on screen size (similar to horizontal-content-card)
    const topOffsets = isSmallMobile ? [0, 15] : isMobile ? [0, 20] : isTablet ? [0, 25] : [0, 30];
    const marginTops = isSmallMobile ? [0, -25] : isMobile ? [0, -30] : isTablet ? [0, -40] : [0, -50];

    return programs.map((_, index) => ({
      top: `${topOffsets[index]}px`,
      marginTop: index > 0 ? `${marginTops[index]}px` : '0',
      zIndex: programs.length - index,
    }));
  }, [isMobile, isSmallMobile, isTablet, programs]);

  useEffect(() => {
    // Skip animations for mobile/tablet
    if (isMobile) return;

    gsap.registerPlugin(ScrollTrigger);

    /**
     * Function to clean up existing ScrollTrigger instances
     *
     * IMPORTANT: Proper ScrollTrigger cleanup is essential because:
     * 1. ScrollTrigger instances persist even after a component unmounts, causing memory leaks
     * 2. Orphaned instances can cause unexpected behavior when components re-render or window resizes
     * 3. Without cleanup, multiple instances can "stack" and interfere with each other
     * 4. ScrollTrigger calculates positions on creation, so they need to be recreated when layout changes
     *
     * Instead of using ScrollTrigger.getAll().forEach(trigger => trigger.kill()) which would
     * kill ALL ScrollTrigger instances globally (including those from other components),
     * we track and clean up only the ones we create in this component.
     */
    const cleanupScrollTriggers = () => {
      // Kill previously created ScrollTrigger instances for this component
      if (scrollTriggersRef.current.length > 0) {
        scrollTriggersRef.current.forEach(trigger => {
          if (trigger) {
            trigger.kill();
          }
        });
        scrollTriggersRef.current = [];
      }

      // Kill any timelines
      if (timelinesRef.current.length > 0) {
        timelinesRef.current.forEach(timeline => {
          if (timeline) {
            timeline.kill();
          }
        });
        timelinesRef.current = [];
      }
    };

    // Wait for DOM to be fully rendered
    const initAnimation = () => {
      // Clean up previous instances first
      cleanupScrollTriggers();

      const items = gsap.utils.toArray('.card') as HTMLElement[];
      const indicators = gsap.utils.toArray('.indicator') as HTMLElement[];
      const indicatorsContainer = document.querySelector('.indicators') as HTMLElement;
      const cardsContainer = document.querySelector('.cards') as HTMLElement;
      const thriveTextElement = document.querySelector('.thrive-text') as HTMLElement;

      if (indicatorsContainer && cardsContainer && items.length > 0) {
        // Get the first card and its image container
        const firstCard = items[0];
        const firstCardImageContainer = firstCard.querySelector('.image-container') as HTMLElement;

        if (firstCardImageContainer) {
          // Get the position of the first card's image container
          const imageRect = firstCardImageContainer.getBoundingClientRect();
          const cardsRect = cardsContainer.getBoundingClientRect();

          // Calculate the top position relative to the cards container
          const topPosition = imageRect.top - cardsRect.top;

          // Set initial position for indicators container
          gsap.set(indicatorsContainer, {
            position: 'absolute',
            zIndex: items.length + 10,
            top: topPosition + 50,
            opacity: 1,
          });

          // Pin the indicators container when the first card is pinned
          const indicatorST = ScrollTrigger.create({
            trigger: firstCard,
            start: `top top+=${firstCard.getAttribute('animation-item')}`,
            pin: indicatorsContainer,
            pinSpacing: false,
          });
          // Store ScrollTrigger reference for later cleanup
          if (indicatorST) {
            scrollTriggersRef.current.push(indicatorST);
          }
        }
      }

      // Set initial indicator state
      gsap.set(indicators[0], { opacity: 1 });
      for (let i = 1; i < indicators.length; i++) {
        gsap.set(indicators[i], { opacity: 0.3 });
      }

      // Special animation for Thrive text to fade in
      if (thriveTextElement) {
        // Set initial opacity to 0
        gsap.set(thriveTextElement, { opacity: 0 });

        // Create a specific animation just for the Thrive text
        const thriveST = ScrollTrigger.create({
          trigger: items[1],
          start: 'top bottom',
          end: 'top top',
          scrub: 0.3,
          onUpdate: self => {
            // Apply a power function to make initial opacity closer to 0
            // Square the progress to get a curved fade-in that starts more transparent
            const opacity = Math.pow(self.progress, 2);
            gsap.set(thriveTextElement, { opacity: opacity });
          },
        });
        // Store ScrollTrigger reference for later cleanup
        if (thriveST) {
          scrollTriggersRef.current.push(thriveST);
        }
      }

      items.forEach((item, index) => {
        const isLast = index === items.length - 1;
        // Set z-index based on index to ensure proper stacking
        gsap.set(item, { zIndex: items.length + index });

        // Find the image container within this card
        const imageContainer = item.querySelector('.image-container');
        // Find the content container within this card
        const contentContainer = item.querySelector('.content-container');

        // Set initial position - each card starts progressively more to the right
        const initialShiftX = index * MARGIN_INCREMENT; // Each card starts 15px more to the right than the previous
        if (imageContainer) {
          gsap.set(imageContainer, {
            transform: `translate3d(${initialShiftX}px, 0, 0)`,
          });
        }

        // Set initial indicator state
        if (index === 0) {
          gsap.set(indicators[index], { opacity: 1 });
        } else {
          gsap.set(indicators[index], { opacity: 0.3 });
        }

        // Set initial content opacity for all cards
        if (contentContainer) {
          gsap.set(contentContainer, {
            opacity: index === 0 ? 1 : 0, // Only the first card starts visible
          });
        }

        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: item,
            start: `top top+=${item.getAttribute('animation-item')}`,
            end: isLast ? 'bottom bottom' : 'bottom top',
            pin: true,
            pinSpacing: false,
            scrub: true,
            onUpdate: self => {
              // Handle fade out of Boost text
              if (contentContainer && index === 0 && !isLast) {
                // First card (Boost) fade out
                const opacity = Math.max(0, 1 - self.progress * 1.3);
                gsap.to(contentContainer, {
                  opacity: opacity,
                  duration: 0.1,
                  ease: 'none',
                });
              }

              if (self.progress > 0.5) {
                // We're transitioning to the next card
                gsap.to(indicators[index], { opacity: 0.3, duration: 0.2 });
                if (index < indicators.length - 1) {
                  gsap.to(indicators[index + 1], { opacity: 1, duration: 0.2 });
                }
              } else {
                // We're still on the current card
                gsap.to(indicators[index], { opacity: 1, duration: 0.2 });
                if (index < indicators.length - 1) {
                  gsap.to(indicators[index + 1], { opacity: 0.3, duration: 0.2 });
                }
              }
            },
            onEnter: () => {
              // Ensure content is visible when entering the section
              if (contentContainer && index === 0) {
                gsap.to(contentContainer, { opacity: 1, duration: 0.3 });
              }
            },
            onLeaveBack: () => {
              // Restore content visibility when scrolling back up
              if (contentContainer && index === 0) {
                gsap.to(contentContainer, { opacity: 1, duration: 0.3 });
              }
            },
          },
        });

        // Store timeline and ScrollTrigger instances separately for proper cleanup
        timelinesRef.current.push(tl);
        if (tl.scrollTrigger) {
          scrollTriggersRef.current.push(tl.scrollTrigger);
        }
      });
    };

    // Use requestAnimationFrame to ensure DOM is painted
    let rafId: number;
    const checkAndInitialize = () => {
      const cardsContainer = document.querySelector('.cards');
      if (cardsContainer) {
        // DOM elements are available, initialize the animation
        initAnimation();
      } else {
        // Try again in the next frame if elements aren't ready yet
        rafId = requestAnimationFrame(checkAndInitialize);
      }
    };

    // Start checking for DOM readiness
    rafId = requestAnimationFrame(checkAndInitialize);

    // Add resize event listener to recalculate animations
    const handleResize = () => {
      // Clean up only our component's ScrollTrigger instances
      cleanupScrollTriggers();

      // Force a ScrollTrigger refresh for all instances on the page
      // This is less aggressive than ScrollTrigger.refresh(true) which can break other animations
      ScrollTrigger.refresh();

      // Delay initialization to ensure DOM updates
      setTimeout(initAnimation, 200);
    };

    // Debounce the resize handler to avoid excessive recalculations
    let resizeTimer: NodeJS.Timeout;
    const debouncedResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(handleResize, 250);
    };

    window.addEventListener('resize', debouncedResize);

    // Clean up all resources when component unmounts
    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', debouncedResize);
      cleanupScrollTriggers();
    };
  }, [isMobile]);

  return (
    <>
      {/* Desktop layout with animations */}
      <div className={buildClassName('join-programs', styles.desktop, styles.component)}>
        <div className={buildClassName('container', styles.container)}>
          <div className={buildClassName('cards', styles.cards)} data-animation="list">
            <div className={buildClassName('indicators', styles.indicators)}>
              {programs.map((_, index) => (
                <div key={index} className={buildClassName('indicator', styles.indicator)} />
              ))}
            </div>
            {programs.map((program, index) => (
              <ProgramCard
                key={index}
                program={program}
                index={index}
                marginIncrement={MARGIN_INCREMENT}
                onButtonClick={onButtonClick}
                thriveTextRef={index === 1 ? thriveTextRef : undefined}
              />
            ))}
          </div>
        </div>
      </div>
      {/* Mobile layout without animations */}
      <div className={buildClassName('join-programs', styles.mobile, styles.component)}>
        <div className={buildClassName('container', styles.container)}>
          <div className={buildClassName('mobile-cards', styles['mobile-cards'])}>
            {programs.map((program, index) => (
              <MobileProgramCard
                key={index}
                program={program}
                index={index}
                onButtonClick={onButtonClick}
                style={{
                  marginTop: getCardOffsets[index].marginTop,
                  position: 'relative',
                  zIndex: getCardOffsets[index].zIndex,
                  top: getCardOffsets[index].top,
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

export default Programs;